import { Classes as BlueprintClasses } from '@blueprintjs/core';
import classNames from 'classnames';
import * as React from 'react';
import { VoiceboxDrawerBody } from 'vet-bones/components/molecules/Voicebox';

import { Sidebar } from 'src/components/sidebar/Sidebar';
import { SidebarSectionType } from 'src/components/sidebarSection/SidebarSection';
import { ProjectSidebarVoiceboxSummary } from 'src/containers/ProjectSidebar/components/ProjectSidebarVoiceboxSummary';
import { useMappingsSection } from 'src/containers/ProjectSidebar/hooks/useMappingsSection';
import { useMosaicProps } from 'src/containers/ProjectSidebar/hooks/useMosaicProps';
import { ProjectSidebarToggleContext } from 'src/containers/ProjectSidebar/hooks/useProjectSidebarToggle';
import { useQuestionsSection } from 'src/containers/ProjectSidebar/hooks/useQuestionsSection';
import { useResourcesSection } from 'src/containers/ProjectSidebar/hooks/useResourcesSection';
import { ProjectSidebarVoiceboxButton } from 'src/containers/ProjectSidebar/ProjectSidebarVoiceboxButton';
import { ProjectSidebarVoiceboxContextProvider } from 'src/containers/ProjectSidebar/ProjectSidebarVoiceboxContextProvider';
import { useIsVoiceboxEnabled } from 'src/utils/hooks/useIsVoiceboxEnabled';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';

import styles from 'src/containers/ProjectSidebar/projectSidebar.module.scss';

const hasSectionItems = (
  section: SidebarSectionType | null,
  hasFilter: boolean
): null | boolean => {
  if (!section) {
    return null;
  }

  return hasFilter ? Boolean(section.itemsCount) : Boolean(section.allCount);
};

export const ProjectSidebar: React.VFC = () => {
  const [filter, setFilter] = React.useState<string>('');

  const isVoiceboxEnabled = useIsVoiceboxEnabled();
  const isVoiceboxOpen = useReduxSelector(
    (state) => state.ui.isVoiceboxDrawerOpen
  );

  const mappingsSection = useMappingsSection(filter);
  const questionsSection = useQuestionsSection(filter);
  const resourceSection = useResourcesSection(filter);

  const hasFilter = Boolean(filter.trim());
  const { collapseOptions, initialValue, splitOptions, setCollapsedById } =
    useMosaicProps(
      filter.trim(),
      hasSectionItems(mappingsSection, hasFilter),
      hasSectionItems(questionsSection, hasFilter),
      Boolean(hasSectionItems(resourceSection, hasFilter))
    );

  const sections = React.useMemo(
    () =>
      [mappingsSection, questionsSection, resourceSection].filter(
        (section): section is SidebarSectionType => Boolean(section)
      ),
    [mappingsSection, questionsSection, resourceSection]
  );

  return (
    <ProjectSidebarVoiceboxContextProvider>
      <ProjectSidebarToggleContext.Provider value={setCollapsedById}>
        {isVoiceboxEnabled ? <ProjectSidebarVoiceboxButton /> : null}
        {isVoiceboxOpen ? (
          <div className={styles.projectSidebarVoiceboxContainer}>
            <div
              className={classNames(
                'vb-voicebox-drawer',
                BlueprintClasses.DRAWER
              )}
            >
              <ProjectSidebarVoiceboxSummary />
              <VoiceboxDrawerBody />
            </div>
          </div>
        ) : (
          <Sidebar
            collapseOptions={collapseOptions}
            filter={filter}
            initialValue={initialValue}
            onFilter={setFilter}
            onToggleCollapsed={setCollapsedById}
            sections={sections}
            splitOptions={splitOptions}
          />
        )}
      </ProjectSidebarToggleContext.Provider>
    </ProjectSidebarVoiceboxContextProvider>
  );
};
